import type { Menu, MenuItem, OptionGroup } from '@/types';
import { resolveModePrice } from '@/features/menu/resolveModePrice';
import type { SharedCartLine } from './tableSessionApi';

// A shared-cart line resolved for display. The server stores only product/option
// UUIDs (it is authoritative for pricing at submit time), so we resolve names,
// option labels AND an estimated dine-in price against the menu here. SC-427: the
// dine-in cart now shows per-line prices + a total before sending (ordering mode).
export interface ResolvedSharedLine {
  lineId: string;
  productUuid: string;
  name: string;
  qty: number;
  optionLabels: string[];
  comment: string;
  addedByNickname: string;
  addedByGuestUuid: string;
  /** Estimated unit price in cents: the dine-in mode price plus option supplements
   *  (same computation as the à-la-carte cart). 0 when the item is unknown to this
   *  menu view. Buffet mode is price-less, so the UI hides this there. */
  unitPrice: number;
}

interface RawOption {
  option_value_uuid?: string;
  qty?: number;
}

// fallbackLabelById is the menu-wide list: a label is the same string wherever the
// option appears, so falling back there only makes an unknown option readable — it
// can never hand over another product's PRICE, which is the reason for the split.
function resolveOptionLabels(
  options: unknown[],
  labelById: Map<string, string>,
  fallbackLabelById: Map<string, string>,
): string[] {
  const out: string[] = [];
  for (const raw of options ?? []) {
    if (!raw || typeof raw !== 'object') continue;
    const opt = raw as RawOption;
    const id = opt.option_value_uuid;
    if (!id) continue;
    const label = labelById.get(id) ?? fallbackLabelById.get(id) ?? id;
    const qty = Math.max(1, Number(opt.qty) || 1);
    out.push(qty > 1 ? `${qty}× ${label}` : label);
  }
  return out;
}

// Sum the option supplements (cents) for one line, mirroring computeUnitPrice:
// each selected option value adds its priceDelta × its qty. Unknown options add 0.
function resolveOptionsPriceCents(options: unknown[], deltaById: Map<string, number>): number {
  let sum = 0;
  for (const raw of options ?? []) {
    if (!raw || typeof raw !== 'object') continue;
    const opt = raw as RawOption;
    const id = opt.option_value_uuid;
    if (!id) continue;
    const qty = Math.max(1, Number(opt.qty) || 1);
    sum += (deltaById.get(id) ?? 0) * qty;
  }
  return sum;
}

// Option labels + supplements, keyed by option-value uuid. Only valid WITHIN the
// product the groups came from: the same option value can be priced differently on
// two products (a per-product override), so a single map shared across the menu
// hands one product's price to another. That is what DF-0258 was. (SC-93 / SC-97)
interface OptionLookup {
  labelById: Map<string, string>;
  deltaById: Map<string, number>;
}

function buildOptionLookup(groups: OptionGroup[]): OptionLookup {
  const labelById = new Map<string, string>();
  const deltaById = new Map<string, number>();
  for (const group of groups) {
    for (const opt of group.options) {
      labelById.set(opt.id, opt.label);
      deltaById.set(opt.id, opt.priceDelta);
    }
  }
  return { labelById, deltaById };
}

/**
 * Map raw shared-cart lines (product/option UUIDs) to display rows by looking up
 * names/labels in the menu. Falls back to the raw UUID when the menu hasn't loaded
 * or a product/option is unknown (e.g. a dine-in-only item not in this menu view).
 */
export function resolveSharedCartLines(
  lines: SharedCartLine[],
  menu: Pick<Menu, 'items' | 'optionGroups'> | undefined,
): ResolvedSharedLine[] {
  const nameById = new Map<string, string>();
  const itemById = new Map<string, MenuItem>();
  if (menu) {
    for (const item of menu.items) {
      nameById.set(item.id, item.name);
      itemById.set(item.id, item);
    }
  }
  // Last-resort lookup, for a product this menu view doesn't carry: the shared
  // list collapses a group to one product's prices, so it is only ever a fallback.
  const sharedLookup = buildOptionLookup(menu?.optionGroups ?? []);
  const lookupByProduct = new Map<string, OptionLookup>();
  const lookupFor = (item: MenuItem | undefined): OptionLookup => {
    if (!item) return sharedLookup;
    const cached = lookupByProduct.get(item.id);
    if (cached) return cached;
    // Per-product resolved groups (SC-93); fall back to the shared list, narrowed
    // to this product's groups, only for older menus that lack item.optionGroups.
    const groups =
      item.optionGroups ??
      item.optionGroupIds
        .map((gid) => menu?.optionGroups.find((g) => g.id === gid))
        .filter((g): g is OptionGroup => Boolean(g));
    const lookup = buildOptionLookup(groups);
    lookupByProduct.set(item.id, lookup);
    return lookup;
  };
  return lines.map((line) => {
    const item = itemById.get(line.product_uuid);
    const lookup = lookupFor(item);
    // Dine-in (sur_place) base price + option supplements, mirroring computeUnitPrice.
    const base = item ? resolveModePrice(item, 'dine_in') : 0;
    const safeBase = Number.isFinite(base) ? base : 0;
    const unitPrice = Math.max(0, safeBase + resolveOptionsPriceCents(line.options, lookup.deltaById));
    return {
      lineId: line.id,
      productUuid: line.product_uuid,
      name: nameById.get(line.product_uuid) ?? line.product_uuid,
      qty: line.quantity,
      optionLabels: resolveOptionLabels(line.options, lookup.labelById, sharedLookup.labelById),
      comment: line.comment ?? '',
      addedByNickname: line.added_by_nickname ?? '',
      addedByGuestUuid: line.added_by_guest_uuid ?? '',
      unitPrice,
    };
  });
}

// A group of one guest's shared-cart lines. Guests are numbered by the order
// their first line appears (lines arrive created_at-ascending) — a simple
// sequence ("客人 1", "客人 2", …) with no nickname required (SC-169).
export interface SharedCartGroup {
  guestUuid: string;
  /** 1-based sequence number for display ("Convive {{index}}"). */
  index: number;
  /** True when this is the current guest's own group ("… (vous)"). */
  isMine: boolean;
  lines: ResolvedSharedLine[];
}

/**
 * Group resolved lines by the guest who added them, in first-appearance order.
 * myGuestUuid (the current device's guest, decoded from its JWT) flags the
 * caller's own group; pass '' when unknown (no group is marked mine).
 */
export function groupSharedCartLinesByGuest(
  lines: ResolvedSharedLine[],
  myGuestUuid: string,
): SharedCartGroup[] {
  const order: string[] = [];
  const linesByGuest = new Map<string, ResolvedSharedLine[]>();
  for (const line of lines) {
    const key = line.addedByGuestUuid || '';
    if (!linesByGuest.has(key)) {
      linesByGuest.set(key, []);
      order.push(key);
    }
    linesByGuest.get(key)!.push(line);
  }
  return order.map((guestUuid, i) => ({
    guestUuid,
    index: i + 1,
    isMine: myGuestUuid !== '' && guestUuid === myGuestUuid,
    lines: linesByGuest.get(guestUuid)!,
  }));
}
